import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLoaderData, useNavigate } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import store from '../store';
import {
    selectStorageSize,
    selectStorageType,
    setPersonaId,
    setStorageSize,
    setStorageType,
} from '../store/bookingSlice';

import Navbar from '../components/Navbar';
import FeaturesAndBenefits from '../components/Questions/FeaturesAndBenefits';
import StorageSizeSelector from '../components/Questions/StorageSizeSelector';
import StorageType from '../components/Questions/StorageType';
import Spinner from '../components/Spinner';

import { useGetPersonasQuery } from '../features/rapidstor/rapidstorApiSlice';

import { findPersonaId } from '../features/helpers';

export interface QuestionnaireData {
    type: string | null;
    size: number | null;
}

function QuestionnairePage() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();

    const storageType = useSelector(selectStorageType);
    const storageSize = useSelector(selectStorageSize);

    const { data, isLoading } = useGetPersonasQuery(location.locationCode);

    const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData>({
        type: null,
        size: null,
    });
    const [navBarLabel, setNavBarLabel] = useState<string>('Select Storage type');

    // Every time the questionnaireData changes, check if both type and size are set
    useEffect(() => {
        if (questionnaireData.type === 'vehicle') {
            store.dispatch(setStorageSize(0));
            navigate(
                `/locations/${
                    location.slug === 'ashford' ? 'ashford' : location.slug
                }/units`
            );
        }
        if (questionnaireData.type === null) {
            setNavBarLabel('Select Storage type');
        }
        if (questionnaireData.type !== null) {
            setNavBarLabel('Estimate your space');
            store.dispatch(setStorageType(questionnaireData.type));
        }
        if (questionnaireData.size !== null) {
            setNavBarLabel('Features & Benefits');
            store.dispatch(setStorageSize(questionnaireData.size));
        }
    }, [questionnaireData, location.slug, navigate]);

    useEffect(() => {
        setQuestionnaireData({
            type: storageType,
            size: storageSize,
        });
    }, [storageType, storageSize]);

    const setQuestionnaireType = (type: string) => {
        store.dispatch(setPersonaId(findPersonaId(type, data)));
        setQuestionnaireData((prev) => {
            return {
                type: type,
                size: prev.size,
            };
        });
    };

    return (
        <>
            <Navbar
                label={navBarLabel}
                storageTypeSelect={questionnaireData.type !== null}
            />
            {isLoading ? (
                <Spinner center size={100} />
            ) : (
                <>
                    {/* STORAGE TYPE SELECTOR */}
                    {questionnaireData.type === null && (
                        <StorageType
                            setQuestionnaireType={setQuestionnaireType}
                            personas={data}
                        />
                    )}
                    {/* SIZE ESTIMATE */}
                    {questionnaireData.type !== null &&
                        questionnaireData.size === null && (
                            <StorageSizeSelector
                                setQuestionnaireType={setQuestionnaireType}
                                setQuestionnaireSize={(size: number) => {
                                    setQuestionnaireData((prev) => {
                                        return {
                                            type: prev.type,
                                            size: size,
                                        };
                                    });
                                }}
                            />
                        )}
                    {questionnaireData.type !== null &&
                        questionnaireData.size !== null && (
                            <FeaturesAndBenefits
                                storageType={questionnaireData.type}
                                personas={data}
                            />
                        )}
                </>
            )}
        </>
    );
}

export default QuestionnairePage;
